import { TechlineStatusCode } from './techline';

export enum CidCvnCheckResult {
    Pass = 'PASS',
    Fail = 'FAIL',
    NoMatchFound = 'NO_MATCH_FOUND',
}

export enum CidCvnScanStatus {
    Success = 'success',
    VinMismatch = 'vin_mismatch',
    ScanError = 'scan_error',
    UploadError = 'upload_error',
}

export type CidCvnEligibilityResponse = {
    cidCvnEligible: boolean;
    vinLockedout: boolean;
};

/** ISO date (`yyyy-MM-dd`) the reference dataset changed; null when nothing has been loaded. */
export type CidCvnReferenceLastUpdatedResponse = {
    sourceDataDate: string | null;
};

type CidCvnRecordLimited = {
    canViewDiagnosticInfo: false;
    cidCvnStatusId: string;
    vin: string;
    result: CidCvnCheckResult;
    reportName: string;
    retailerId: string;
    createdTimestamp: string;
    cpoInd: boolean;
};

type CidCvnRecordFull = {
    canViewDiagnosticInfo: true;
    cidCvnStatusId: string;
    vin: string;
    cid: string;
    cvn: string;
    fileName: string;
    result: CidCvnCheckResult;
    reportName: string;
    retailerId: string;
    createdTimestamp: string;
    createdAccountId: string;
    createdAccountName: string;
    cpoInd: boolean;
    techlineCaseNumber?: string;
    techlineCaseStatus?: TechlineStatusCode;
};

export type CidCvnRecord = CidCvnRecordLimited | CidCvnRecordFull;
export type CidCvnListResponse = CidCvnRecord[];

export type CidCvnValidateImageResponse = {
    cidCvnStatusId: string;
    vin: string;
    cid: string;
    cvn: string;
    fileName: string;
    result: CidCvnCheckResult;
    reportName: string;
    createdTimestamp: string;
    createdAccountId: string;
    createdAccountName: string;
    cpoInd: boolean;
    retailerId: string;
};

export type CidCvnOcrAlternative = {
    value: string;
    score: number;
    reasons: string[];
    preselected: boolean;
};

export type CidCvnOcrField = {
    top: CidCvnOcrAlternative;
    alternatives: CidCvnOcrAlternative[];
    raw_ocr: string;
    confidence: number;
};

export type CidCvnVinReference = {
    provided: string;
    status: string;
    distance: number;
    convertible: boolean;
    reasons: string[];
};

export type CidCvnOcrResponse = {
    session_id: string;
    vin: CidCvnOcrField;
    cin: CidCvnOcrField;
    cvn: CidCvnOcrField;
    latency_ms: number;
    vin_reference: CidCvnVinReference;
    extracted_vin: string;
};

type CidCvnReportLimited = {
    canViewDiagnosticInfo: false;
    cidCvnStatusId: string;
    vin: string;
    result: CidCvnCheckResult;
    reportName: string;
    createdTimestamp: string;
    cpoInd: boolean;
};

type CidCvnReportFull = {
    canViewDiagnosticInfo: true;
    cidCvnStatusId: string;
    vin: string;
    cid: string;
    cvn: string;
    fileName: string;
    result: CidCvnCheckResult;
    reportName: string;
    createdTimestamp: string;
    createdAccountId: string;
    createdAccountName: string;
    imageUrl: string;
    cpoInd: boolean;
    retailerId: string;
    retailerInfo: CidCvnRetailerInfo;
    techlineCaseNumber?: string;
    techlineCaseStatus?: TechlineStatusCode;
};

export type CidCvnReportResponse = CidCvnReportLimited | CidCvnReportFull;

export type CidCvnRetailerInfo = {
    code: string;
    district: string;
    name: string;
    region: string;
    zone: string;
    role: string;
};

export enum ChangeConfidenceClassification {
    Exact = 'EXACT',
    WithinTolerance = 'WITHIN_TOLERANCE',
    Reject = 'REJECT',
}

export type ChangeConfidenceResponse = {
    cvn: {
        original: string;
        changed: string;
        confidence: number;
        reasons: string[];
        differences: string[];
        classification: ChangeConfidenceClassification;
    };
    cin: {
        original: string;
        changed: string;
        confidence: number;
        reasons: string[];
        differences: string[];
        classification: ChangeConfidenceClassification;
    };
};
